import { FC, useContext, useEffect, useState } from 'react';
import { isToday } from 'date-fns';

import { H3, Loader, TertiaryButton, useIntersectionObserver } from '@rikstv/shared-components';

import { Program } from '../../forces/channels/channel.types';
import { Link } from '../../router/Link';
import { commonRoutes } from '../../router/router.path';
import { isDateInRange } from '../../utils/date/date.utils';
import { ProviderImage } from '../providerImage/ProviderImage';

import { TvGuideMiniProgram } from './mini/TvGuideMiniProgram';
import { TvGuideEmptyContainer } from './ShowMorePrograms/TvGuideEmptyContainer';
import { TvGuideProgram } from './ShowMorePrograms/TvGuideProgram';
import { ChannelWithoutProgram, TvGuideContext } from './TvGuideContext';
import { usePrograms } from './usePrograms';

import styles from './TVGuideChannel.module.css';

interface Props {
  channel: ChannelWithoutProgram;
  maxPrograms?: number;
  showFullDayButton?: boolean;
  isMiniTVGuide?: boolean;
  displayEarlierProgramsCount?: number;
}

export const TvGuideChannel: FC<Props> = ({
  channel,
  maxPrograms = 8,
  showFullDayButton = true,
  isMiniTVGuide = false,
  displayEarlierProgramsCount = 0,
}) => {
  const [isInViewport, setIsInViewport] = useState(false);
  const { programs } = usePrograms({ epgUrl: channel._links.epg.href, shouldFetch: isInViewport });

  const onIntersect = (entires: IntersectionObserverEntry[]) =>
    setIsInViewport(entires.some(entry => entry.isIntersecting));
  const fallback = () => setIsInViewport(true);
  const [targetRef] = useIntersectionObserver(onIntersect, { rootMargin: '0px', threshold: [0.0, 1.0] }, fallback);

  const { selectedDate } = useContext(TvGuideContext);
  const [displayFullDay, setDisplayFullDay] = useState(isToday(selectedDate));
  const [displayPrograms, setDisplayPrograms] = useState<Program[]>([]);
  const currentProgram = !!programs
    ? programs.find(p => isDateInRange(new Date(), p.time, p.duration) && p.name !== 'Ingen sending')
    : undefined;

  useEffect(() => {
    if (programs) {
      if (displayFullDay || !isToday(selectedDate)) {
        return setDisplayPrograms(programs);
      } else {
        const currentProgramIndex = currentProgram ? programs.indexOf(currentProgram) : 0;
        const takeFromIndex =
          displayEarlierProgramsCount === 0
            ? currentProgramIndex
            : Math.max(currentProgramIndex - displayEarlierProgramsCount, 0);
        setDisplayPrograms(programs.slice(takeFromIndex, takeFromIndex + maxPrograms));
      }
    }
  }, [selectedDate, programs, displayFullDay, currentProgram, maxPrograms, displayEarlierProgramsCount]); // Add displayEarlierProgramsCount to dependencies

  useEffect(() => {
    setDisplayFullDay(!isToday(selectedDate));
  }, [selectedDate]);

  if (!programs) {
    return (
      <div ref={targetRef} className={styles.placeholder}>
        <Loader size="large" />
      </div>
    );
  }

  return (
    <div>
      <Link
        aria-label={`Se ${currentProgram?.name} på ${channel.serviceName} live`}
        to={commonRoutes.watchLive(channel.channelId, channel.serviceName)}
        className={styles.tvGuideChannelLink}>
        <div aria-hidden className={styles.tvGuideChannel} itemScope itemType="http://schema.org/BroadcastService">
          <ProviderImage
            className={styles.channelLogo}
            src={`${channel._links.logopack.href}square.svg`}
            size="large"
          />
          <H3 className={styles.tvGuideChannelTitle}>{channel.serviceName}</H3>
        </div>
      </Link>
      <ul className={styles.tvGuidePrograms}>
        {displayPrograms.map((program, idx) =>
          isMiniTVGuide ? (
            <TvGuideMiniProgram
              key={`${program.name}-${program.time}-${idx}`}
              program={program}
              isCurrentProgram={program.time === currentProgram?.time}
            />
          ) : (
            <TvGuideProgram
              key={`${program.name}-${program.time}-${idx}`}
              program={program}
              isCurrentProgram={program.time === currentProgram?.time}
            />
          )
        )}
        {displayPrograms.length === 0 && <TvGuideEmptyContainer channelName={channel.serviceName} />}
        {isToday(selectedDate) && showFullDayButton && (
          <li className={styles.showAllButton}>
            <TertiaryButton type="button" onClick={() => setDisplayFullDay(!displayFullDay)}>
              {displayFullDay ? 'Vis færre' : 'Vis hele dagen'}
            </TertiaryButton>
          </li>
        )}
      </ul>
    </div>
  );
};
